import isEqual from "lodash/isEqual";
import React, { memo, useEffect, useMemo, useRef } from "react";

import VeneerMap, { MapProvider, useMap } from "veneer/Map";
import styled from "veneer/styled";

import {
  type DevicesFeature,
  type DevicesFeatureCollection,
  type Marker,
  type SpiderLinesFeatureCollection,
  addResetControl,
  createGeoJSONFromMarkers,
  createSpiderLines,
  fitMapToFeatures,
  jitterMarkers,
} from "../utils/action";
import {
  INITIAL_CENTER,
  INITIAL_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
} from "../utils/constant";
import { DevicesLayers, DevicesSources } from "../utils/layer";

const ACCESS_TOKEN =
  "pk.eyJ1IjoidmlzdGFyLW1lZGlhIiwiYSI6ImNtb2xpMHN1NTBrMTgyeHIyOHUwd2NnZTQifQ.5bkSDLeZHqSWoHbQNnDTNQ";

interface MapProps {
  markers: Marker[];
  maxZoom?: number;
  minZoom?: number;
  onViewDeviceDetails?: (deviceId: string) => void;
}

export function Map({
  markers,
  maxZoom = MAX_ZOOM,
  minZoom = MIN_ZOOM,
  onViewDeviceDetails,
}: MapProps) {
  const jitteredMarkers = useMemo(() => jitterMarkers(markers), [markers]);

  const devicesData: DevicesFeatureCollection = useMemo(
    () => createGeoJSONFromMarkers(jitteredMarkers),
    [jitteredMarkers],
  );

  const spiderLinesData: SpiderLinesFeatureCollection = useMemo(
    () => createSpiderLines(jitteredMarkers),
    [jitteredMarkers],
  );

  return (
    <MapContainer>
      <MapProvider>
        <VeneerMap
          accessToken={ACCESS_TOKEN}
          height="100%"
          initialCenter={INITIAL_CENTER}
          initialZoom={INITIAL_ZOOM}
          minZoom={minZoom}
        >
          <DevicesSources
            devicesData={devicesData}
            spiderLinesData={spiderLinesData}
          />
          <DevicesLayers
            maxZoom={maxZoom}
            onViewDeviceDetails={onViewDeviceDetails}
          />
          <DevicesEffects features={devicesData.features} maxZoom={maxZoom} />
        </VeneerMap>
      </MapProvider>
    </MapContainer>
  );
}

interface DevicesEffectsProps {
  features: DevicesFeature[];
  maxZoom: number;
}

//Prevent unnecessary re-renders by memoizing and doing a deep comparison on features
const DevicesEffects = memo(
  function DevicesEffects({ features, maxZoom }: DevicesEffectsProps) {
    const { current: mapRef } = useMap();
    const map = mapRef.getMap();

    const featuresRef = useRef<DevicesFeature[]>(features);
    const controlsAddedRef = useRef(false);

    useEffect(() => {
      featuresRef.current = features;

      if (!map || !features.length) {
        return;
      }

      fitMapToFeatures(features, map, maxZoom);
    }, [features, map, maxZoom]);

    useEffect(() => {
      if (!map || controlsAddedRef.current) {
        return;
      }

      controlsAddedRef.current = true;

      addResetControl(map, () => {
        if (!featuresRef.current.length) {
          return;
        }
        fitMapToFeatures(featuresRef.current, map, maxZoom);
      });
    }, [map, maxZoom]);

    return null;
  },
  (prev, next) => {
    if (prev.maxZoom !== next.maxZoom) {
      return false;
    }
    return isEqual(prev.features, next.features);
  },
);

export const MapContainer = styled("div")(({ theme }) => ({
  flexGrow: 1,
  minHeight: 400,
  "& .mapboxgl-map": {
    minHeight: 400,
  },
  "& .mapboxgl-popup": {
    zIndex: 2,
  },
  "& .mapboxgl-popup-content": {
    borderRadius: theme.shape.borderRadius * 4,
    boxShadow:
      "0 1px 18px 0 rgba(0,0,0,.12), 0 6px 10px 0 rgba(0,0,0,.14), 0 3px 5px -1px rgba(0,0,0,.2)",
    overflow: "hidden",
    padding: 0,
  },
  "& .mapboxgl-popup-content > *:first-of-type": {
    overflowY: "auto",
  },
  "& .mapboxgl-popup-tip": {
    display: "none",
  },
}));
